import { useEffect, useRef } from 'react';
import { createEngineHandle } from './engine-handle';
import { asFloors } from './v8/orders';
import type { FlyOrder } from './v8/orders';
import type { MapEngineHandle } from './engine-handle';
import type { MapStats, Orientation, Sector, Wash } from './controller';
import type { RenderEdge, RenderNode } from '@versesverse/core';

/*
 * THE ONE IMPERATIVE COMPONENT — a thin shell around the canvas and the draw engine.
 *
 * React owns the element and the lifecycle; nothing inside the canvas. The map is drawn with Canvas 2D
 * — which the browser rasterises and composites on the GPU — because that look (soft bridge glows,
 * bowed antialiased edges, crisp text) is the one we want. The performance work is not swapping the
 * rasteriser but getting the DRAW off the main thread: an OffscreenCanvas is transferred to a worker
 * that owns the whole draw loop, so a pan never janks the UI (see engine-handle / map.worker). Where a
 * worker is unavailable it falls back to the same engine on the main thread — same pixels either way.
 *
 * This component only turns input into engine commands. A drag draws interacting=true (no labels, no
 * measuring); a beat after it ends, one settle draw brings the labels back. `focusId` centres the map
 * on a node chosen elsewhere (the search box). A tap is resolved by the engine (it holds the geometry),
 * so hit-testing is async.
 */
export interface MapCanvasProps {
  nodes: RenderNode[];
  edges: RenderEdge[];
  /**
   * Where each scripture's names are thickest — the faint wash behind the field of figures.
   *
   * Optional because it is a layer, not a requirement: a caller that has no washes draws a map
   * without them rather than none at all. Map-level rather than per-node, so it cannot ride along
   * on `nodes`.
   */
  washes?: Wash[];
  /** The wedges and their labels — which direction means which scripture. Optional, like the washes. */
  sectors?: Sector[];
  /** Told when the figure in front of the reader changes — pushed from the engine, never polled. */
  onOrientation?: (at: Orientation | null) => void;
  onSelect?: (id: string | null) => void;
  /**
   * WHAT IS UNDER THE POINTER, so a reader can learn a name before committing a click.
   *
   * `MapHoverCard` has been built, styled and tested since the port, and it is fed by `V8Map` alone —
   * so on the renderer every reader actually gets, the card rendered for nobody. It is not gated on
   * the flag; it was simply never given anything, which is the quieter half of "ported, covered and
   * unreachable".
   *
   * The id only. This canvas has no language and no corpus vocabulary; the caller has both and
   * already builds exactly this sentence for the search row, so sending an id keeps one speller.
   */
  onHover?: (id: string | null) => void;
  /** Where to put the card, in client coordinates. */
  onHoverAt?: (at: { x: number; y: number }) => void;
  focusId?: string | null;
  /**
   * Where the opening flight's camera should be — a world point, or null for the whole field, at a
   * multiple of the zoom that fits the field.
   *
   * Declarative like `focusId` rather than an imperative handle, so the arrival's schedule stays in
   * one place (`useTour`) and this component keeps its one job: turning props and input into engine
   * commands. A new object flies; the same object again does nothing.
   */
  /* An order may name an absolute zoom, which this renderer has no notion of — `asFloors` is where
     that is decided, and its comment says what happens to one. */
  fly?: FlyOrder | null;
  /**
   * What the reader has walked, and what stands one step from it.
   *
   * The map is uncovered by walking rather than filtered, and this is what makes that true rather
   * than merely said. Omitted — or with nothing walked — the whole field draws at full strength,
   * which is what a map nobody has walked has to look like.
   *
   * MEMOISE THE TWO LISTS, as `nodes` and `edges` must be: they are compared by identity, and a
   * fog rebuilt every render would send a redraw every render, for a list that changes only when
   * the reader opens a figure.
   */
  fog?: { walked: readonly string[]; within: readonly string[]; mix: number };
  /** The selected node — its edges are lit in its colour and the rest of the web recedes. */
  selectedId?: string | null;
}

/** What the screenshot harness reads to confirm the map controller attached (see reader-gate.ts). */
interface MapDebug {
  stats(): MapStats;
}

declare global {
  interface Window {
    __map?: MapDebug;
  }
}

const EMPTY_STATS: MapStats = { draws: 0, visibilityComputes: 0, strokeBatches: 0, labelMeasures: 0, labelsDrawn: 0, zoom: 0, camX: 0, camY: 0, nodesDrawn: 0, edgesDrawn: 0, strandGrowths: 0 };

/**
 * How much of the screen the open node panel covers, so a focused node lands where it can be seen and
 * not under it. Mirrors the panel CSS var --panel-w: a right rail of min(28rem, 92vw) on a wide screen,
 * a bottom sheet of ~62vh on a phone (≤640px). Approximate — the root font is taken as 16px — which is
 * all the camera needs to keep the node clear of the panel.
 */
function panelInset(): { right: number; bottom: number } {
  if (typeof window === 'undefined') return { right: 0, bottom: 0 };
  const w = window.innerWidth;
  if (w > 640) return { right: Math.min(28 * 16, w * 0.92), bottom: 0 };
  return { right: 0, bottom: window.innerHeight * 0.62 };
}

/**
 * WHERE THE CHROME COVERS THE MAP, measured off the real elements.
 *
 * The canvas fills the viewport and every control floats over it, so a name placed under the
 * search box is drawn and then covered — and the placer counts it as placed, so the figure that
 * could have been named there loses its turn to a name nobody can read.
 *
 * Read off `[data-map-chrome]` rather than a list of selectors kept here, so a control that starts
 * covering the map declares it where it is written, and this never goes looking for a class that
 * was renamed. Measured relative to the CANVAS, because that is the space the labels are placed in.
 *
 * An element with no size — a collapsed panel, something not yet mounted — is skipped: a zero-area
 * band blocks nothing, and sending it is a rectangle the worker has to test against for ever.
 */
function chromeBands(canvas: HTMLCanvasElement): { x0: number; y0: number; x1: number; y1: number }[] {
  const origin = canvas.getBoundingClientRect();
  const bands: { x0: number; y0: number; x1: number; y1: number }[] = [];
  for (const el of document.querySelectorAll('[data-map-chrome]')) {
    const r = el.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) continue;
    bands.push({ x0: r.left - origin.left, y0: r.top - origin.top, x1: r.right - origin.left, y1: r.bottom - origin.top });
  }
  return bands;
}

function cssSize(canvas: HTMLCanvasElement): { width: number; height: number; dpr: number } {
  const rect = canvas.getBoundingClientRect();
  return {
    width: Math.max(1, Math.round(rect.width)),
    height: Math.max(1, Math.round(rect.height)),
    dpr: window.devicePixelRatio || 1,
  };
}

export function MapCanvas({ nodes, edges, washes, sectors, onOrientation, onSelect, onHover, onHoverAt, focusId, fly, fog, selectedId }: MapCanvasProps): React.JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const handleRef = useRef<MapEngineHandle | null>(null);
  /* The OffscreenCanvas can be transferred only once, but StrictMode mounts effects twice in dev
     (mount → cleanup → mount). So the handle is built once and kept in a ref, and teardown is deferred
     one tick — a StrictMode remount reclaims it, a real unmount lets the timer terminate the worker. */
  const destroyTimerRef = useRef<number | undefined>(undefined);
  /* Kept current without re-running the setup effect, so a new onSelect identity does not tear the
     canvas down and rebuild it. */
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const onHoverRef = useRef(onHover);
  onHoverRef.current = onHover;
  const onHoverAtRef = useRef(onHoverAt);
  onHoverAtRef.current = onHoverAt;
  /* Held in a ref for the same reason: the engine is built ONCE, and a new callback identity on
     every render must not tear down the canvas and the worker to install it. */
  const onOrientationRef = useRef(onOrientation);
  onOrientationRef.current = onOrientation;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (destroyTimerRef.current !== undefined) { window.clearTimeout(destroyTimerRef.current); destroyTimerRef.current = undefined; }

    let handle = handleRef.current;
    if (!handle) {
      const { width, height, dpr } = cssSize(canvas);
      /* ?worker=0 forces the main-thread engine — a debug escape hatch for A/B measuring the worker. */
      const preferWorker = new URLSearchParams(window.location.search).get('worker') !== '0';
      handle = createEngineHandle(canvas, nodes, edges, washes ?? [], sectors ?? [], width, height, dpr, preferWorker, at => onOrientationRef.current?.(at));
      handleRef.current = handle;
    }
    if (!handle) return; // no 2D context (e.g. jsdom) — the element still mounts
    const engine = handle;
    let settleTimer: number | undefined;

    window.__map = { stats: () => engine.stats() ?? EMPTY_STATS };

    const settle = (): void => {
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => engine.setInteracting(false), 140);
    };

    /* Told once on mount and again whenever anything resizes — the chrome is fluid (clamp() paddings,
       a "More" toggle that opens, a panel that slides in), so its bands are not a constant. */
    engine.setChrome(chromeBands(canvas));

    const observer = new ResizeObserver(() => {
      const { width, height, dpr } = cssSize(canvas);
      engine.setViewport(width, height, dpr);
      engine.setChrome(chromeBands(canvas));
    });
    observer.observe(canvas);
    /* Watch the chrome itself, not only the canvas: opening the legend or the language switcher
       changes what is covered without changing the size of anything the canvas would notice. */
    for (const el of document.querySelectorAll('[data-map-chrome]')) observer.observe(el);

    let dragging = false;
    let last = { x: 0, y: 0 };
    let moved = 0;

    /*
     * HOVER IS A WORKER ROUND TRIP, so it is throttled twice over.
     *
     * `engine.hitTest` returns a promise — the map draws off-thread on an OffscreenCanvas, so each
     * ask is a message. A pointermove fires far faster than that, so this keeps ONE in flight and
     * drops every move that arrives while it is out. The answer is about where the pointer was when
     * the question was asked, which is what a card following a pointer needs.
     *
     * Nothing is asked while dragging: a reader panning the map is not pointing at anything, and the
     * card chasing the pan would be noise over the thing they are moving.
     */
    let asking = false;
    let hoveredId: string | null = null;
    const askWhatIsThere = (e: PointerEvent): void => {
      if (asking || onHoverRef.current === undefined) return;
      asking = true;
      const rect = canvas.getBoundingClientRect();
      engine.hitTest(e.clientX - rect.left, e.clientY - rect.top)
        .then(id => {
          /* Reported only when it CHANGES, so a card that is already right is not re-rendered on
             every frame the pointer rests. */
          if (id !== hoveredId) {
            hoveredId = id;
            onHoverRef.current?.(id);
          }
          if (id !== null) onHoverAtRef.current?.({ x: e.clientX, y: e.clientY });
        })
        .catch(() => {})
        .finally(() => { asking = false; });
    };
    const onPointerLeave = (): void => {
      if (hoveredId === null) return;
      hoveredId = null;
      onHoverRef.current?.(null);
    };
    const onPointerDown = (e: PointerEvent): void => {
      dragging = true;
      moved = 0;
      last = { x: e.clientX, y: e.clientY };
      engine.setInteracting(true);
      canvas.setPointerCapture(e.pointerId);
    };
    const onPointerMove = (e: PointerEvent): void => {
      if (!dragging) { askWhatIsThere(e); return; }
      const dx = e.clientX - last.x;
      const dy = e.clientY - last.y;
      last = { x: e.clientX, y: e.clientY };
      moved += Math.abs(dx) + Math.abs(dy);
      engine.pan(dx, dy);
    };
    const onPointerUp = (e: PointerEvent): void => {
      if (!dragging) return;
      dragging = false;
      engine.setInteracting(false);
      if (moved < 5 && onSelectRef.current) {
        const rect = canvas.getBoundingClientRect();
        /* A hit test that fails is a tap that selected nothing, which is what a tap on empty map
           does anyway. Held so a failed one is not a rejection nobody catches. */
        engine.hitTest(e.clientX - rect.left, e.clientY - rect.top)
          .then(id => onSelectRef.current?.(id))
          .catch(() => {});
      }
    };
    const onWheel = (e: WheelEvent): void => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      engine.setInteracting(true);
      engine.zoomAt(e.clientX - rect.left, e.clientY - rect.top, e.deltaY < 0 ? 1.1 : 1 / 1.1);
      settle();
    };

    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('pointerleave', onPointerLeave);
    canvas.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      window.clearTimeout(settleTimer);
      observer.disconnect();
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', onPointerUp);
      canvas.removeEventListener('pointerleave', onPointerLeave);
      canvas.removeEventListener('wheel', onWheel);
      if (window.__map) delete window.__map;
      /* Defer teardown so a StrictMode remount can reclaim the handle; a real unmount has no remount,
         so the timer fires and the worker is terminated. */
      destroyTimerRef.current = window.setTimeout(() => {
        handleRef.current?.destroy();
        handleRef.current = null;
        destroyTimerRef.current = undefined;
      }, 0);
    };
  }, [nodes, edges, washes, sectors]);

  /* Centre the map on a node chosen elsewhere — the search result. Its own effect, so it does not
     rebuild the canvas, and it reaches the engine through the ref the setup effect filled. */
  useEffect(() => {
    if (!focusId) return;
    const engine = handleRef.current;
    if (!engine) return;
    /* Respect a reader who has asked the system for less motion — for them the camera cuts. */
    const reduceMotion = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    engine.focusOn(focusId, { animate: !reduceMotion, inset: panelInset() });
  }, [focusId]);

  /* The flight's camera. Its own effect for the same reason `focusId` has one: it must not rebuild
     the canvas, and it reaches the engine through the ref the setup effect filled. */
  useEffect(() => {
    if (!fly) return;
    /* A reader who asked the system for less motion gets each beat placed, not flown to. */
    const reduceMotion = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    handleRef.current?.flyTo(fly.to, asFloors(fly), reduceMotion ? 0 : fly.durMs);
  }, [fly]);

  /* The fog. Its own effect, on the two lists' identity — so a caller must memoise them, exactly as
     it must for `nodes` and `edges` above. A fog rebuilt every render would ask for a redraw every
     render, over a list that changes when the reader opens a figure and at no other time. */
  const walked = fog?.walked;
  const within = fog?.within;
  const mix = fog?.mix ?? 1;
  useEffect(() => {
    handleRef.current?.setFog(walked ?? [], within ?? [], mix);
  }, [walked, within, mix]);

  /* Light the selected node's connections in its colour. The engine keeps the selection, so a redraw
     during a pan preserves the highlight; only a change of selection asks for a draw. */
  useEffect(() => {
    handleRef.current?.setSelected(selectedId ?? null);
  }, [selectedId]);

  return <canvas ref={canvasRef} className="map-canvas" aria-hidden="true" />;
}
